import React, { Component } from 'react';
import { func, bool, string, shape, arrayOf } from 'prop-types';
import { Switch, Route, Redirect } from 'react-router-dom';
import {
  Address,
  AddressCoBorrower,
  AmountPurpose,
  AppType,
  DOB,
  Email,
  Income,
  IncomeAdditional,
  JAAgreement,
  LoanType,
  Name,
  Password,
  Phone,
  SSN,
  AccountValidation,
  Living,
} from './pages';
import ContinueApp from '@shared_modules/continue-app';
import context from './serverContext';
import { hasMatchingArrayElements } from './utils/util';
import { FLOW_ERRORS } from './utils/constants';

class Routing extends Component {
  getDefaultStartState = () => {
    const { allSteps, location, history, error } = this.props;
    const startState = allSteps[0];
    const qs = location.search;
    if (error && error.length && allSteps.length > 1) {
      history.goBack();
      return null;
    }
    return <Redirect to={`${startState}${qs}`} />;
  };

  shouldRenderURL = () => {
    const { allSteps, location, showUnloadPrompt, nextPage, error, isRepeatsUser } = this.props;
    const { isLoggedIn, dm } = context;
    const index = allSteps.indexOf(location.pathname);

    // There are only four cases we allow the targetPath
    // 1. it is the first step in allSteps, always allow
    // 2. it is another step in allSteps and the step is created due to the flow (where showUnloadPrompt is set to true)
    // 3. it is another step in allSteps after a freshLoad triggered by click next button (where showUnloadPrompt is set to false) and
    //    b.  the state has next page or
    //    c.  the state has error
    // 4. When a user logs into PI1 and is not DM and is a repeats user

    return (
      index === 0 ||
      (index > 0 && (showUnloadPrompt || nextPage || (error && error.length))) ||
      (!isLoggedIn && !dm && isRepeatsUser)
    );
  };

  render() {
    const {
      formProps,
      formProps: { checkEmailErrors },
      fields,
      error,
      ssnRequired,
      showSentilinkSSNConsent,
    } = this.props;

    const foundError = error && error.find(err => err.error);
    const errorCode = foundError && foundError.error;
    const errorTypesToCheck = [FLOW_ERRORS.LOAN_APP_IN_PROGRESS, FLOW_ERRORS.REJECTED_BORROWER];

    // Validate check email errors and submit errors to show new loan app in progress and reject borrower errors
    const hasMappedError = !!checkEmailErrors?.length || foundError?.error;
    const isLaip =
      !context.dm.isDm &&
      checkEmailErrors &&
      hasMatchingArrayElements(checkEmailErrors, errorTypesToCheck) &&
      hasMappedError;
    const hasRejectedError = errorCode && errorTypesToCheck.includes(errorCode) && hasMappedError;
    const isLaipOrRejectedError = isLaip || hasRejectedError;

    return (
      <Switch>
        <Route exact path="/" render={() => <>{this.getDefaultStartState()}</>} />
        <Route
          path="/loan-type"
          render={() =>
            this.shouldRenderURL() ? (
              <LoanType
                {...formProps}
                {...fields.loan}
                loanAmount={fields.prequal.loanAmount}
                dm={context.dm}
                experiments={context.experiments}
                primaryName={fields.primary.name}
              />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/app-type"
          render={() =>
            this.shouldRenderURL() ? (
              <AppType {...formProps} {...fields.loan} loanAmount={fields.prequal.loanAmount} />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/primary-name"
          render={() =>
            this.shouldRenderURL() ? (
              <Name {...formProps} {...fields.primary.name} loanAmount={fields.prequal.loanAmount} dm={context.dm} />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/primary-address"
          render={() =>
            this.shouldRenderURL() ? (
              <Address
                {...formProps}
                address={fields.primary.address}
                isSecondLoan={context.isFromSecondLoanFlow}
                dm={context.dm}
                primaryName={fields.primary.name}
              />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        {/* // Keep this for v4 of Phone in PI1 test slated for Q1 2020 */}
        <Route
          path="/primary-phone"
          render={() =>
            this.shouldRenderURL() ? (
              <Phone
                {...formProps}
                phone={fields.primary.phone.phone}
                phoneType={fields.primary.phone.phoneType}
                phoneSource={fields.primary.phone.phoneSource}
              />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/primary-dob"
          render={() =>
            this.shouldRenderURL() ? (
              <DOB
                {...formProps}
                {...fields.primary.dob}
                name={fields.primary.name.firstName}
                state={fields.primary.address.state}
              />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/primary-income"
          render={() =>
            this.shouldRenderURL() ? (
              <Income {...formProps} {...fields.primary.income} />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/primary-living"
          render={() =>
            this.shouldRenderURL() ? (
              <Living
                {...formProps}
                {...fields.primary.homeStatus}
                {...fields.primary.homeStatusExpense}
                {...fields.primary.name}
              />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/primary-income-additional"
          render={() =>
            this.shouldRenderURL() ? (
              <IncomeAdditional {...formProps} {...fields.primary.incomeAdditional} />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/secondary-name"
          render={() =>
            this.shouldRenderURL() ? (
              <Name
                {...formProps}
                {...fields.secondary.name}
                email={fields.email}
                emailSecondary={fields.emailSecondary}
                isSecondLoan={context.isFromSecondLoanFlow}
              />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/secondary-address"
          render={() =>
            this.shouldRenderURL() ? (
              <AddressCoBorrower
                {...formProps}
                address={fields.secondary.address}
                secondaryName={fields.secondary.name.firstName}
                primaryAddress={fields.primary.address}
              />
            ) : (
              this.getDefaultStartState()
            )
          }
        />
        <Route
          path="/secondary-dob"
          render={() =>
            this.shouldRenderURL() ? (
              <DOB
                {...formProps}
                {...fields.secondary.dob}
                name={fields.secondary.name.firstName}
                state={fields.secondary.address.state}
              />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/secondary-income"
          render={() =>
            this.shouldRenderURL() ? (
              <Income {...formProps} {...fields.secondary.income} firstName={fields.secondary.name.firstName} />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/secondary-living"
          render={() =>
            this.shouldRenderURL() ? (
              <Living
                {...formProps}
                {...fields.secondary.homeStatus}
                {...fields.secondary.homeStatusExpense}
                {...fields.secondary.name}
              />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/secondary-email"
          render={() =>
            this.shouldRenderURL() ? (
              <Email
                {...formProps}
                email={fields.email}
                emailSecondary={fields.emailSecondary}
                agreements={fields.agreements}
                agreementsSecondary={fields.agreementsSecondary}
                primaryFirstName={fields.primary.name.firstName}
                secondaryFirstName={fields.secondary.name.firstName}
                errors={error}
              />
            ) : (
              this.getDefaultStartState()
            )
          }
        />
        <Route
          path="/loan-intent"
          render={() =>
            this.shouldRenderURL() ? (
              <AmountPurpose
                {...formProps}
                {...fields.prequal}
                dm={context.dm}
                state={fields.primary.address.state}
                loanIntentSubmitted={!!fields.loanIntentSubmitted}
                isLoanAmountPrefill={!!fields.isLoanAmountPrefill}
                isLoanPurposePrefill={!!fields.isLoanPurposePrefill}
              />
            ) : (
              this.getDefaultStartState()
            )
          }
        />
        <Route
          path="/email"
          render={() =>
            this.shouldRenderURL() ? (
              <Email
                {...formProps}
                email={fields.email}
                emailSecondary={fields.emailSecondary}
                agreements={fields.agreements}
                agreementsSecondary={fields.agreementsSecondary}
                primaryFirstName={fields.primary.name.firstName}
                secondaryFirstName={fields.secondary.name.firstName}
                errors={error}
              />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/agreements"
          render={() =>
            this.shouldRenderURL() ? (
              <JAAgreement
                {...formProps}
                email={fields.email}
                password={fields.password}
                agreements={fields.agreements}
                {...fields.loan}
              />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/ssn"
          render={() =>
            this.shouldRenderURL() ? (
              <SSN
                {...formProps}
                primarySSNRequired={ssnRequired.primary}
                secondarySSNRequired={ssnRequired.secondary}
                primaryName={fields.primary.name}
                secondaryName={fields.secondary && fields.secondary.name}
                showSentilinkSSNConsent={showSentilinkSSNConsent}
              />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/password"
          render={() =>
            this.shouldRenderURL() ? (
              <Password
                {...formProps}
                password={fields.password}
                email={fields.email}
                primaryFirstName={fields.primary.name.firstName}
                errors={error}
                agreements={fields.agreements}
              />
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        <Route
          path="/continue-app"
          render={() =>
            this.shouldRenderURL() ? (
              isLaipOrRejectedError ? (
                <AccountValidation {...formProps} errors={error} />
              ) : (
                <ContinueApp {...formProps} errors={{ err: error }} />
              )
            ) : (
              <>{this.getDefaultStartState()}</>
            )
          }
        />
        {/* Catchall route : Primarily to address backward compatibility of old routes */}
        <Route path="*" render={() => <>{this.getDefaultStartState()}</>} />
      </Switch>
    );
  }
}

Routing.propTypes = {
  allSteps: arrayOf(string).isRequired,
  location: shape({
    pathname: string,
    search: string,
  }).isRequired,
  error: arrayOf(
    shape({
      error: string,
      message: string,
    })
  ),
  history: shape({
    goBack: func,
  }).isRequired,
  nextPage: string,
  formProps: shape({
    isJoint: bool,
    isSecondary: bool,
    nextLocation: func,
    previousLocation: func,
    saveValues: func,
  }).isRequired,
  fields: shape().isRequired,
  ssnRequired: shape({
    primary: bool,
    secondary: bool,
  }).isRequired,
  showUnloadPrompt: bool.isRequired,
  loanType: string.isRequired,
};

Routing.defaultProps = {
  error: [],
  nextPage: undefined,
};

export default Routing;

require('/var/lib/jenkins/workspace/borrower-apply-ui_Release_0/src/pages/pi1-1QAAT/pi1-1QAAT.scss');